import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import styled from "styled-components";
import Button from "@mui/material/Button";
import { gql } from "@apollo/client";
import { client } from "../../../client";
import { uploadFileWithPresignedUrl } from "../../../utils/s3Uploader";
import FileUploader from "../admin-document/FileUploader";

const ModalContainer = styled(Box)`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80%;
  height: 80%;
  background-color: white; /* Change to white background */
  padding: 20px;
  z-index: 100;
  border: 1px solid #ccc; /* Add a subtle border */
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1); /* Add a subtle box shadow */
`;

const UserInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center; /* Center-align all children */
  margin-top: 30px;
  margin-bottom: 30px;
`;

const UserInfoItem = styled.div`
  display: flex; /* Display children in a row */
  flex-direction: row;
  margin-bottom: 10px; /* Add margin between child components */
`;

const NoticeText = styled.textarea`
  width: 200px;
  height: 150px;
`;

const ModifyNoticeAdmin = ({
  isOpen,
  data,
  handleCloseClick,
  refetchParentData,
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [editableData, setEditableData] = useState({});

  useEffect(() => {
    if (data) {
      setEditableData({
        id: data.id,
        visible: data.visible,
        title: data.title,
        content: data.content,
        object_url: data.object_url,
        importance: data.importance,
      });
    }
  }, [data]);

  const handleInputChange = (key, value) => {
    setEditableData((prevData) => ({
      ...prevData,
      [key]: value,
    }));
  };

  const handleRemoveImage = () => {
    if (window.confirm("첨부된 파일을 제거하시겠습니까?")) {
      setEditableData((prev) => ({ ...prev, object_url: null }));
      setSelectedFile(null);
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      let objectUrlToSubmit = editableData.object_url;

      if (selectedFile) {
        objectUrlToSubmit = await uploadFileWithPresignedUrl(
          client,
          selectedFile
        );
      }

      const MODIFY_NOTICE_ADMIN = gql`
        mutation modifyNoticeByAdmin(
          $id: Int!
          $visible: Boolean
          $title: String
          $importance: Importance
          $content: String
          $object_url: String
        ) {
          modifyNoticeByAdmin(
            id: $id
            visible: $visible
            title: $title
            importance: $importance
            content: $content
            object_url: $object_url
          ) {
            message
          }
        }
      `;

      const variables = {
        id: editableData.id,
        visible: editableData.visible,
        title: editableData.title,
        content: editableData.content,
        importance: editableData.importance,
        object_url: objectUrlToSubmit,
      };

      await client.mutate({
        mutation: MODIFY_NOTICE_ADMIN,
        variables: variables,
      });

      alert("수정이 완료되었습니다.");
      refetchParentData();
      handleCloseClick();
    } catch (err) {
      alert(`수정 중 오류가 발생했습니다: ${err.message}`);
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const updatedTime = (params) => {
    const date = new Date(parseInt(params));
    return date.toLocaleString("ko-kr");
  };

  if (!isOpen) return null;

  return (
    <ModalContainer>
      <h2> 공지사항 수정 </h2>

      <UserInfoContainer>
        {Object.keys(data).map((key) => {
          if (key === "__typename") return null;
          return (
            <div key={key}>
              {key === "title" ||
              key === "content" ||
              key === "object_url" ||
              key === "created_at" ||
              key === "updated_at" ||
              key === "importance" ||
              key === "visible" ? (
                <UserInfoItem>
                  <div>
                    <Box mr={"5px"}>{key}: </Box>
                  </div>
                  <div>
                    {key === "visible" ? (
                      <select
                        value={editableData[key] ? "true" : "false"}
                        onChange={(e) =>
                          handleInputChange(key, e.target.value === "true")
                        }
                      >
                        <option value="true">노출</option>
                        <option value="false">비노출</option>
                      </select>
                    ) : key === "importance" ? (
                      <select
                        value={editableData.importance}
                        onChange={(e) => handleInputChange(key, e.target.value)}
                      >
                        <option value={"GENERAL"}>GENERAL</option>
                        <option value={"EMERGENCY"}>EMERGENCY</option>
                        <option value={"IMPORTANT"}>IMPORTANT</option>
                      </select>
                    ) : key === "updated_at" || key === "created_at" ? (
                      <Box>{updatedTime(data[key])}</Box>
                    ) : key === "object_url" ? (
                      <div>
                        {editableData.object_url ? (
                          <>
                            <Box
                              component="img"
                              src={editableData.object_url}
                              sx={{
                                maxHeight: "150px",
                                maxWidth: "150px",
                                objectFit: "contain",
                                mb: 1,
                              }}
                            />
                            <Button
                              size="small"
                              color="error"
                              onClick={handleRemoveImage}
                              sx={{ display: "block", mb: 1 }}
                            >
                              이미지 제거
                            </Button>
                          </>
                        ) : (
                          <p>첨부된 이미지가 없습니다.</p>
                        )}
                        <FileUploader
                          selectedFiles={selectedFile ? [selectedFile] : []}
                          setSelectedFiles={(files) =>
                            setSelectedFile(files[0] || null)
                          }
                        />
                      </div>
                    ) : key === "content" ? (
                      <Box>
                        <NoticeText
                          type="text"
                          value={editableData[key]}
                          onChange={(e) =>
                            handleInputChange(key, e.target.value)
                          }
                        />
                      </Box>
                    ) : (
                      <input
                        type="text"
                        value={editableData[key]}
                        onChange={(e) => handleInputChange(key, e.target.value)}
                      />
                    )}
                  </div>
                </UserInfoItem>
              ) : (
                <UserInfoItem>
                  {key} : {data[key]}
                </UserInfoItem>
              )}
            </div>
          );
        })}
      </UserInfoContainer>

      <Button variant="contained" onClick={handleSave} disabled={isLoading}>
        {isLoading ? "저장 중..." : "저장"}
      </Button>

      <Button onClick={() => handleCloseClick()}> 닫기 </Button>
    </ModalContainer>
  );
};

export default ModifyNoticeAdmin;
